import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Settings, Bell, Shield, Database, Loader2, RefreshCw, Wifi, WifiOff, Stethoscope, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function EdenServicesConfig() {
  const [edenServices, setEdenServices] = useState([]);
  const [configuredServices, setConfiguredServices] = useState([]);
  const [edenHealth, setEdenHealth] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [healthRes, servicesRes, configRes, syncRes] = await Promise.all([
        axios.get(`${API_URL}/api/eden/health`, { withCredentials: true }).catch(() => ({ data: { status: 'disconnected' } })),
        axios.get(`${API_URL}/api/eden/services`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/eden/config`, { withCredentials: true }).catch(() => ({ data: { configured_services: [] } })),
        axios.get(`${API_URL}/api/eden/sync-status`, { withCredentials: true }).catch(() => ({ data: {} })),
      ]);
      setEdenHealth(healthRes.data);
      setEdenServices(servicesRes.data);
      setConfiguredServices(configRes.data.configured_services || []);
      setSyncStatus(syncRes.data);
    } catch (err) {
      console.error('Eden config fetch error', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const isServiceEnabled = (serviceId) => {
    return configuredServices.some(s => s.service_id === serviceId && s.enabled);
  };

  const toggleService = (service) => {
    setConfiguredServices(prev => {
      const existing = prev.find(s => s.service_id === service.id);
      if (existing) {
        return prev.map(s => s.service_id === service.id ? { ...s, enabled: !s.enabled } : s);
      }
      return [...prev, { service_id: service.id, service_name: service.name, enabled: true }];
    });
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/eden/config`, {
        configured_services: configuredServices,
        polling_enabled: true,
      }, { withCredentials: true });
      toast.success('Eden service configuration saved');
    } catch (err) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const enabledCount = configuredServices.filter(s => s.enabled).length;
  const connected = edenHealth?.status === 'connected';

  // Deduplicate services by id
  const uniqueServices = edenServices.filter(
    (s, i, arr) => arr.findIndex(x => x.id === s.id) === i
  );

  if (loading) {
    return (
      <Card className="border-border shadow-sm" data-testid="eden-config-loading">
        <CardContent className="py-10 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border shadow-sm" data-testid="eden-services-config">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-emerald-50">
              <Stethoscope className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <CardTitle className="font-heading text-lg">Eden Services</CardTitle>
              <CardDescription>
                Select which Eden check-in services auto-create consult cards
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {connected ? (
              <Badge className="gap-1.5 bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100" data-testid="eden-connected-badge">
                <Wifi className="w-3 h-3" />
                Connected
              </Badge>
            ) : (
              <Badge variant="destructive" className="gap-1.5" data-testid="eden-disconnected-badge">
                <WifiOff className="w-3 h-3" />
                Disconnected
              </Badge>
            )}
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={fetchAll} data-testid="eden-refresh-button">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Sync status */}
        {syncStatus && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground px-1" data-testid="eden-sync-info">
            <div className={`w-2 h-2 rounded-full ${syncStatus.is_running ? 'bg-emerald-500 animate-pulse' : 'bg-slate-300'}`} />
            Sync: {syncStatus.is_running ? 'Active (polling every 30s)' : 'Inactive'}
            {syncStatus.last_seen && <span>· Last update: {new Date(syncStatus.last_seen).toLocaleTimeString('en-IN')}</span>}
          </div>
        )}

        {/* Service list */}
        {uniqueServices.length === 0 ? (
          <div className="text-center py-6 text-sm text-muted-foreground">
            {connected ? 'No services found in Eden.' : 'Connect to Eden to see available services.'}
          </div>
        ) : (
          <div className="space-y-2">
            {uniqueServices.filter(s => s.status === 'active').map(service => (
              <div
                key={service.id}
                className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                  isServiceEnabled(service.id) ? 'border-emerald-200 bg-emerald-50/50' : 'border-slate-200'
                }`}
                data-testid={`eden-service-${service.id}`}
              >
                <div className="flex items-center gap-3">
                  <div>
                    <p className="text-sm font-medium">{service.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {service.type === 'queue_based' ? 'Queue-based' : 'Time-based'}
                      {service.duration_minutes ? ` · ${service.duration_minutes} min` : ''}
                      {service.category ? ` · ${service.category}` : ''}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={isServiceEnabled(service.id)}
                  onCheckedChange={() => toggleService(service)}
                  data-testid={`eden-service-toggle-${service.id}`}
                />
              </div>
            ))}
          </div>
        )}

        {/* Save button */}
        <div className="flex items-center justify-between pt-2 border-t">
          <p className="text-xs text-muted-foreground">
            {enabledCount} service{enabledCount !== 1 ? 's' : ''} will trigger consult cards
          </p>
          <Button
            onClick={saveConfig}
            disabled={saving}
            className="rounded-lg bg-emerald-600 hover:bg-emerald-700"
            data-testid="eden-save-config-button"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
            Save Configuration
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function BackupCard() {
  const [generating, setGenerating] = useState(false);

  const handleBackup = async () => {
    setGenerating(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/backup`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      const date = new Date().toISOString().split('T')[0];
      link.href = url;
      link.setAttribute('download', `dermaclinic-backup-${date}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Backup downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Backup failed');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Card className="border-border shadow-sm" data-testid="backup-card">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-[#E6EFEA]">
              <Database className="w-5 h-5 text-[#0E5132]" />
            </div>
            <div>
              <CardTitle className="font-heading text-lg">Database Backup</CardTitle>
              <CardDescription>
                Download a full backup of all patients, consult cards, prescriptions, and clinical data as JSON.
              </CardDescription>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">Includes all collections except user credentials.</p>
          <Button
            onClick={handleBackup}
            disabled={generating}
            className="rounded-lg bg-[#0E5132] hover:bg-[#063D2F]"
            data-testid="generate-backup-button"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Database className="w-4 h-4 mr-1.5" />}
            Download Backup
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function SettingsPage() {
  const settingsSections = [
    {
      icon: Settings,
      title: 'General Settings',
      description: 'Clinic name, address, and contact information',
      status: 'Coming Soon'
    },
    {
      icon: Bell,
      title: 'Notifications',
      description: 'Configure email and SMS notification preferences',
      status: 'Coming Soon'
    },
    {
      icon: Shield,
      title: 'Security',
      description: 'Password policies and two-factor authentication',
      status: 'Coming Soon'
    },
    {
      icon: Database,
      title: 'Data Management',
      description: 'Backup, export, and data retention settings',
      status: 'Coming Soon'
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-3xl font-medium tracking-tight text-foreground">
          Settings
        </h1>
        <p className="text-muted-foreground mt-1">
          Configure system preferences and clinic settings
        </p>
      </div>

      {/* Eden Services Configuration — functional */}
      <EdenServicesConfig />

      {/* Database Backup */}
      <BackupCard />

      {/* Other settings — placeholder */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {settingsSections.map((section) => (
          <Card key={section.title} className="border-border shadow-sm card-interactive">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="p-3 rounded-xl bg-muted">
                  <section.icon className="w-5 h-5 text-muted-foreground" />
                </div>
                <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">
                  {section.status}
                </span>
              </div>
              <CardTitle className="font-heading text-lg mt-4">{section.title}</CardTitle>
              <CardDescription>{section.description}</CardDescription>
            </CardHeader>
          </Card>
        ))}
      </div>
    </div>
  );
}
